/**
 * CurrencySettings - System-wide currency catalogue manager
 * Lets superadmin add/edit custom currencies + base rates against USD.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Loader2, Coins, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      ...opts.headers,
    },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Request failed');
  return res.json();
};

const emptyForm = { code: '', symbol: '', name: '', rate_to_usd: '' };

export const CurrencySettings = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const { data: catalogue, isLoading } = useQuery({
    queryKey: ['fx-catalogue'],
    queryFn: () => apiFetch('/fx/catalogue'),
  });
  const items = catalogue?.items || [];

  const { data: rates } = useQuery({
    queryKey: ['fx-manual-rates'],
    queryFn: () => apiFetch('/fx/manual-rates'),
  });
  const ratesByCode = React.useMemo(() => {
    const map = {};
    (rates?.rates || []).forEach(r => {
      if (r.from_ccy === 'USD') map[r.to_ccy] = r.rate;
    });
    return map;
  }, [rates]);

  const upsertMutation = useMutation({
    mutationFn: (payload) => apiFetch('/fx/catalogue', { method: 'POST', body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      toast.success(`Saved ${data.code}`);
      queryClient.invalidateQueries({ queryKey: ['fx-catalogue'] });
      queryClient.invalidateQueries({ queryKey: ['fx-manual-rates'] });
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (code) => apiFetch(`/fx/catalogue/${code}`, { method: 'DELETE' }),
    onSuccess: (_, code) => {
      toast.success(`Removed ${code}`);
      queryClient.invalidateQueries({ queryKey: ['fx-catalogue'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const handleEdit = (item) => {
    setEditing(item.code);
    setForm({
      code: item.code,
      symbol: item.symbol || '',
      name: item.name || '',
      rate_to_usd: ratesByCode[item.code] ? String(ratesByCode[item.code]) : '',
    });
    setDialogOpen(true);
  };

  const handleNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    upsertMutation.mutate({
      code: form.code.trim().toUpperCase(),
      symbol: form.symbol.trim() || form.code.trim().toUpperCase(),
      name: form.name.trim() || form.code.trim().toUpperCase(),
      rate_to_usd: form.rate_to_usd ? parseFloat(form.rate_to_usd) : null,
    });
  };

  const filtered = items.filter(i => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return i.code.toLowerCase().includes(q) || (i.name || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="currency-settings-panel">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-amber-500" /> Currency Catalogue
              </CardTitle>
              <CardDescription>
                Every currency picker in the ecosystem (campaigns, churches, school fees, POS, donations)
                reads from this list. Add any custom currency code with a symbol, name and exchange rate against USD.
              </CardDescription>
            </div>
            <Button onClick={handleNew} className="shrink-0" data-testid="add-currency-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add Currency
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search code or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="currency-search"
            />
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Code</TableHead>
                    <TableHead className="w-20">Symbol</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Rate (1 USD =)</TableHead>
                    <TableHead className="w-32">Source</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(item => (
                    <TableRow key={item.code} data-testid={`currency-row-${item.code}`}>
                      <TableCell className="font-mono font-semibold">{item.code}</TableCell>
                      <TableCell className="font-mono text-lg">{item.symbol}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {ratesByCode[item.code] !== undefined ? Number(ratesByCode[item.code]).toFixed(4) : <span className="text-slate-400">— live —</span>}
                      </TableCell>
                      <TableCell>
                        {item.custom
                          ? <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Custom</Badge>
                          : <Badge variant="outline" className="bg-slate-50 text-slate-600">Built-in</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => handleEdit(item)} title="Edit"
                          data-testid={`edit-currency-${item.code}`}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        {item.custom && (
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                            onClick={() => {
                              if (window.confirm(`Remove ${item.code} (${item.name})?`)) deleteMutation.mutate(item.code);
                            }}
                            title="Delete" data-testid={`delete-currency-${item.code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing}` : 'Add Currency'}</DialogTitle>
            <DialogDescription>
              ISO codes preferred (3 letters). Custom codes work for community currencies like FaithCoin.
              Rate is optional — leave blank to use live rates.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Code</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().slice(0, 5) })}
                placeholder="BWP / NAD / FCN"
                disabled={!!editing}
                maxLength={5}
                data-testid="currency-form-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Symbol</Label>
                <Input
                  value={form.symbol}
                  onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                  placeholder="₦ · P · R · ƒ"
                  data-testid="currency-form-symbol"
                />
              </div>
              <div>
                <Label className="text-xs">Rate (1 USD =)</Label>
                <Input
                  type="number" min="0" step="0.0001"
                  value={form.rate_to_usd}
                  onChange={(e) => setForm({ ...form, rate_to_usd: e.target.value })}
                  placeholder="13.50"
                  data-testid="currency-form-rate"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Display Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Botswana Pula"
                data-testid="currency-form-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={upsertMutation.isPending} data-testid="currency-form-save">
              {upsertMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurrencySettings;
